<section class="ephemeris-lanes">
  <header class="ephemeris-lanes__header">
    <h3 class="ephemeris-lanes__title">{{ t('title') }}</h3>
    <div class="ephemeris-lanes__zoom" role="group">
      @for (level of zoomLevels; track level) {
        <button
          type="button"
          class="ephemeris-lanes__zoom-btn"
          [class.is-active]="zoom() === level"
          (click)="setZoom(level)"
        >
          {{ t(zoomLabelKeys[level]) }}
        </button>
      }
      <!-- Jaren: enkel met alleen de transpersoonlijke planeten zichtbaar. Geen
           `disabled`-attribuut, zodat de tooltip ook in de uit-stand verschijnt. -->
      <button
        type="button"
        class="ephemeris-lanes__zoom-btn"
        [class.is-active]="isYearView()"
        [class.is-disabled]="!yearsAvailable()"
        [attr.aria-disabled]="!yearsAvailable()"
        [title]="yearZoomTooltip()"
        (click)="setZoom(yearZoom)"
      >
        {{ t(zoomLabelKeys[yearZoom]) }}
      </button>
    </div>
    <hd-transit-legend-filter class="ephemeris-lanes__filter-legend" [showChiron]="showChiron()" />
    <div class="ephemeris-lanes__legend">
      @if (isYearView()) {
        <!-- Jaren: een blok = poortperiode (eerste intrede tot laatste uittrede);
             de sterkere tint = de doorgangen, de lichte = even in een buurpoort. -->
        <span class="legend-item">
          <span
            class="legend-swatch legend-swatch--year-period"
            aria-hidden="true"
            [style.border-color]="yearLegendColors.stroke"
            [style.background]="yearLegendColors.period"
          ></span>{{ t('legend.yearPeriod') }}
        </span>
        <span class="legend-item">
          <span
            class="legend-swatch"
            aria-hidden="true"
            [style.background]="yearLegendColors.visit"
          ></span>{{ t('legend.yearInGate') }}
        </span>
        <span class="legend-item">
          <span
            class="legend-swatch"
            aria-hidden="true"
            [style.background]="yearLegendColors.away"
          ></span>{{ t('legend.yearAway') }}
        </span>
      } @else {
        <span class="legend-item">
          <span class="legend-swatch legend-swatch--retro" aria-hidden="true"></span>{{ t('legend.retrograde') }}
        </span>
        <span class="legend-item">
          <span class="legend-station" aria-hidden="true">S</span>{{ t('legend.stationRetrograde') }}
        </span>
        <span class="legend-item">
          <span class="legend-station legend-station--direct" aria-hidden="true">S</span>{{ t('legend.stationDirect') }}
        </span>
      }
      <!-- Klik op Nu/Selectie scrollt de lanes zodat de marker op het
           golden-ratio-punt staat (iets meer toekomst dan verleden). -->
      <span
        class="legend-item legend-item--clickable"
        role="button"
        tabindex="0"
        [attr.aria-label]="t('legend.scrollToNowAria')"
        (click)="scrollToNow()"
        (keydown)="onLegendItemKeydown($event, 'now')"
      >
        <span class="legend-swatch legend-swatch--now" aria-hidden="true"></span>{{ t('legend.nowLine') }}
      </span>
      <span
        class="legend-item legend-item--clickable"
        [class.is-disabled]="selectedTimeMs() == null"
        role="button"
        [attr.tabindex]="selectedTimeMs() != null ? 0 : null"
        [attr.aria-label]="t('legend.scrollToSelectionAria')"
        [attr.aria-disabled]="selectedTimeMs() == null"
        (click)="scrollToSelection()"
        (keydown)="onLegendItemKeydown($event, 'selection')"
      >
        <span class="legend-swatch legend-swatch--selection" aria-hidden="true"></span>{{ t('legend.selectionLine') }}
      </span>
    </div>
  </header>

  @if (fallbackNotice() != null) {
    <p class="ephemeris-lanes__notice" role="status">{{ fallbackNotice() }}</p>
  }

  <div class="ephemeris-lanes__stage" [style.min-height.px]="stageMinHeightPx()">
    @if (showBody()) {
      <!-- Ruler-markup gedeeld door header- en footer-strook (gespiegeld via `bottom`). -->
      <ng-template #rulerSvg let-bottom="bottom">
        <svg
          class="layer-ruler"
          [class.layer-ruler--bottom]="bottom"
          [attr.width]="viewportWidthPx()"
          [attr.height]="rulerHeight"
          role="img"
          [attr.aria-label]="t('rulerAria')"
        >
          @for (tick of rulerTicks(); track tick.timeMs) {
            <!-- Onzichtbaar hit-vlak over het streepje: draagt de tijd-tooltip. -->
            <g class="ruler-tick-group">
              <title>{{ tick.tooltip }}</title>
              <rect
                class="ruler-tick-hit"
                [attr.x]="tick.xPx - 4.5"
                [attr.y]="bottom ? 0 : rulerHeight - rulerTickLength(tick)"
                width="9"
                [attr.height]="rulerTickLength(tick)"
              />
              <line
                class="ruler-tick"
                [class.ruler-tick--major]="tick.major"
                [class.ruler-tick--minor]="tick.label == null"
                [attr.x1]="tick.xPx"
                [attr.x2]="tick.xPx"
                [attr.y1]="bottom ? 0 : rulerHeight - rulerTickLength(tick)"
                [attr.y2]="bottom ? rulerTickLength(tick) : rulerHeight"
              />
            </g>
            @if (tick.label != null) {
              <text
                class="ruler-label"
                [attr.x]="tick.xPx"
                [attr.y]="bottom ? rulerHeight - 6 : 10"
                text-anchor="middle"
              >{{ tick.label }}</text>
            }
          }
          <!-- De nu-lijn loopt over de ruler-stroken door, met een marker naar de lanes. -->
          @if (rulerNowX() != null) {
            <line
              class="ruler-now"
              [attr.x1]="rulerNowX()"
              [attr.x2]="rulerNowX()"
              y1="0"
              [attr.y2]="rulerHeight"
            />
            <path
              class="ruler-now-marker"
              [attr.d]="bottom ? rulerNowMarkerBottom : rulerNowMarkerTop"
              [attr.transform]="'translate(' + rulerNowX() + ',' + (bottom ? 0 : rulerHeight - 5) + ')'"
            />
          }
          <!-- Selectielijn (klik op de tijdlijn): blauw, met het tijdstip erbij;
               in de header een plus-knop om er een gebeurtenis aan toe te voegen.
               Rechterklik op lijn/label wist de selectie. -->
          @if (rulerSelectedX() != null) {
            <line
              class="ruler-selection"
              [attr.x1]="rulerSelectedX()"
              [attr.x2]="rulerSelectedX()"
              y1="0"
              [attr.y2]="rulerHeight"
              (contextmenu)="clearSelection($event)"
            />
            @if (!bottom) {
              <g
                class="ruler-add-event"
                [attr.transform]="'translate(' + rulerSelectedX() + ',0)'"
                tabindex="0"
                role="button"
                [attr.aria-label]="addEventAria()"
                (click)="openEventAdd($event)"
                (keydown)="onAddEventKeydown($event)"
              >
                <rect class="ruler-add-event__rect" x="-16" y="5" width="14" height="14" rx="3" />
                <text class="ruler-add-event__plus" x="-9" y="15" text-anchor="middle">+</text>
              </g>
            }
            <!-- Het label zit een regel onder/voorbij de terugkerende tick-labels. -->
            <text
              class="ruler-selection-label"
              [attr.x]="rulerSelectedX() + 4"
              [attr.y]="bottom ? 8 : 22"
              text-anchor="start"
              (contextmenu)="clearSelection($event)"
            >
              <title>{{ selectionTimeTooltip() }}</title>
              {{ selectionTimeLabel() }}
            </text>
          }
        </svg>
      </ng-template>

      <!-- Ruler-header: datum/tijd-ticks boven de lanes; sticky in de verticale
           scroll-context van de panel-wrapper (eigen laag — later ook events/markers). -->
      <div class="ephemeris-lanes__ruler ephemeris-lanes__ruler--top">
        <div class="ephemeris-lanes__ruler-spacer" [style.width.px]="planetsWidthPx()"></div>
        <div class="ephemeris-lanes__ruler-viewport" (click)="onRulerTimelineClick($event)">
          <ng-container *ngTemplateOutlet="rulerSvg; context: { bottom: false }"></ng-container>
        </div>
      </div>

      <div class="ephemeris-lanes__body">
        <!-- Sticky planeetkolom (HTML) naast de scrollende SVG. -->
        <div #planetsEl class="ephemeris-lanes__planets">
          @for (lane of lanes(); track lane.planetKey) {
            <div
              class="planet-cell"
              [style.height.px]="laneHeightPx()"
              [style.border-left-color]="lane.stripeColor"
            >
              <span class="planet-cell__glyph" aria-hidden="true">{{ lane.glyph }}</span>
              <span class="planet-cell__name">{{ lane.name }}</span>
            </div>
          }
        </div>

        <!-- scroll/wheel/pointermove worden programmatisch buiten de Angular-zone
             geregistreerd (constructor-effect resp. onPanPointerDown): per event
             geen app-brede change detection; pointermove alleen tijdens een drag. -->
        <div
          #scrollEl
          class="ephemeris-lanes__scroll"
          [class.is-panning]="panning()"
          tabindex="0"
          (keydown)="onScrollKeydown($event)"
          (pointerdown)="onPanPointerDown($event)"
          (pointerup)="onPanPointerUp($event)"
          (pointercancel)="onPanPointerUp($event)"
        >
          <svg
            class="ephemeris-lanes__svg"
            [attr.width]="svgWidth()"
            [attr.height]="svgHeight()"
            role="img"
            [attr.aria-label]="t('title')"
            (click)="onLanesTimelineClick($event)"
          >
            <defs>
              <pattern
                id="hd-ephemeris-retro-hatch"
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill="transparent" />
                <line x1="0" y1="0" x2="0" y2="6" class="ephemeris-lanes__hatch-line" />
              </pattern>
            </defs>

            <!-- Haakje stap 3: kanaal-/definitie-vensters als tijdvenster-band boven de lanes. -->
            <g class="layer-windows"></g>

            <g class="layer-blocks">
              @for (lane of lanes(); track lane.planetKey) {
                <g
                  class="lane"
                  [attr.transform]="'translate(0,' + lane.yPx + ')'"
                  [attr.aria-label]="t('laneAria', { planet: lane.name })"
                >
                  <line
                    class="lane__separator"
                    x1="0"
                    [attr.x2]="svgWidth()"
                    [attr.y1]="laneHeightPx() - 0.5"
                    [attr.y2]="laneHeightPx() - 0.5"
                  />
                  <!-- Jaren-weergave: poortperiodes in twee verspringende rijen. -->
                  @for (box of lane.yearBoxes; track box.id) {
                    <g
                      class="year-box"
                      tabindex="0"
                      role="button"
                      [attr.aria-label]="box.tooltip"
                      (click)="openYearBoxPopup(lane, box, $event)"
                      (keydown)="onYearBoxKeydown(lane, box, $event)"
                    >
                      <title>{{ box.tooltip }}</title>
                      <rect
                        [attr.x]="box.xPx"
                        [attr.y]="box.yPx"
                        [attr.width]="box.widthPx"
                        [attr.height]="box.heightPx"
                        [attr.fill]="lane.yearAwayFill"
                        rx="3"
                      />
                      @for (visit of box.visits; track visit.xPx) {
                        <rect
                          class="year-box__visit"
                          [attr.x]="visit.xPx"
                          [attr.y]="box.yPx"
                          [attr.width]="visit.widthPx"
                          [attr.height]="box.heightPx"
                          [attr.fill]="lane.yearVisitFill"
                        />
                      }
                      <!-- Kader na de doorgangen, zodat die de rand niet bedekken. -->
                      <rect
                        class="year-box__frame"
                        [attr.x]="box.xPx"
                        [attr.y]="box.yPx"
                        [attr.width]="box.widthPx"
                        [attr.height]="box.heightPx"
                        [attr.stroke]="lane.stripeColor"
                        fill="none"
                        rx="3"
                      />
                      @if (box.title != null) {
                        <text
                          class="year-box__title"
                          [attr.x]="box.labelXPx"
                          [attr.y]="box.yPx + (box.dates != null ? yearTitleYPx : box.heightPx / 2 + 4)"
                          text-anchor="middle"
                        >{{ box.title }}</text>
                      }
                      @if (box.dates != null) {
                        <text
                          class="year-box__dates"
                          [attr.x]="box.labelXPx"
                          [attr.y]="box.yPx + yearDatesYPx"
                          text-anchor="middle"
                        >{{ box.dates }}</text>
                      }
                    </g>
                  }
                  @for (item of lane.items; track item.id) {
                    <g
                      class="lane-block"
                      [class.lane-block--gate]="item.kind === 'gate'"
                      [class.lane-block--line]="item.kind === 'line'"
                      [class.lane-block--merged]="item.kind === 'merged'"
                      [class.is-hovered]="item.kind === 'gate' && hoveredGateId() === item.id"
                      tabindex="0"
                      role="button"
                      [attr.aria-label]="item.ariaLabel"
                      (mouseenter)="onBlockHover(item)"
                      (mouseleave)="onBlockHover(null)"
                      (click)="openBlockPopup(lane, item, $event)"
                      (keydown)="onBlockKeydown(lane, item, $event)"
                    >
                      <rect
                        class="lane-block__rect"
                        [attr.x]="item.xPx"
                        [attr.y]="item.yPx"
                        [attr.width]="item.widthPx"
                        [attr.height]="item.heightPx"
                        [attr.fill]="item.kind === 'gate' ? lane.gateFill : lane.lineFill"
                        [attr.stroke]="lane.stripeColor"
                        rx="2"
                      />
                      <!-- Retrograde-arcering exact van station tot station (ook een deel van het blok). -->
                      @for (hatch of item.hatches; track hatch.xPx) {
                        <rect
                          class="lane-block__hatch"
                          [attr.x]="hatch.xPx"
                          [attr.y]="item.yPx"
                          [attr.width]="hatch.widthPx"
                          [attr.height]="item.heightPx"
                          fill="url(#hd-ephemeris-retro-hatch)"
                          rx="2"
                        />
                      }
                      @if (item.label; as label) {
                        <text
                          class="lane-block__label"
                          [class.lane-block__label--gate]="item.kind === 'gate'"
                          [class.lane-block__label--merged]="item.kind === 'merged'"
                          [class.lane-block__label--vertical]="lane.verticalGateLabels && item.kind === 'gate'"
                          [attr.x]="item.labelXPx"
                          [attr.y]="item.labelYPx"
                          [attr.text-anchor]="item.labelAnchor"
                          [attr.transform]="
                            lane.verticalGateLabels && item.kind === 'gate'
                              ? 'rotate(-90 ' + item.labelXPx + ' ' + item.labelYPx + ')'
                              : null
                          "
                          >{{ label.prefix }}<tspan class="lane-block__label-strong">{{ label.strong }}</tspan>{{ label.suffix }}</text>
                      }
                    </g>
                  }
                  @for (station of lane.stations; track station.id) {
                    <g
                      class="station"
                      [class.station--direct]="station.turnsTo === 'direct'"
                      [attr.transform]="'translate(' + station.xPx + ',0)'"
                      role="img"
                      [attr.aria-label]="station.ariaLabel"
                    >
                      <title>{{ station.ariaLabel }}</title>
                      <circle class="station__dot" cy="5.5" r="4.5" />
                      <text class="station__s" y="8.5" text-anchor="middle">S</text>
                    </g>
                  }
                  @for (loop of lane.loops; track loop.id) {
                    <path
                      class="retro-loop"
                      [attr.d]="loop.pathD"
                      [attr.stroke]="lane.stripeColor"
                      role="img"
                      [attr.aria-label]="loop.ariaLabel"
                    >
                      <title>{{ loop.ariaLabel }}</title>
                    </path>
                  }
                  <!-- Ná de blokken: smalle, onzichtbare hover-zones op elke
                       overgang, zodat het exacte wisseltijdstip opvraagbaar is. -->
                  @for (boundary of lane.boundaries; track boundary.id) {
                    <g class="lane-boundary" [attr.transform]="'translate(' + boundary.xPx + ',0)'">
                      <title>{{ boundary.tooltip }}</title>
                      <rect
                        class="lane-boundary__hit"
                        [attr.x]="-boundaryHitWidthPx / 2"
                        [attr.y]="boundaryYPx"
                        [attr.width]="boundaryHitWidthPx"
                        [attr.height]="boundaryHeightPx"
                      />
                    </g>
                  }
                </g>
              }
            </g>

            <!-- Haakje stap 7: scrubber-/event-markers. -->
            <g class="layer-markers"></g>

            <!-- Selectielijn (klik op de tijdlijn): kanaal-highlight-blauw;
                 de brede transparante hit-lijn draagt de hover-tooltip én de
                 rechterklik waarmee de selectie verdwijnt. -->
            <g class="layer-selection">
              @if (selectedX() != null) {
                <line
                  class="selection-line-hit"
                  [attr.x1]="selectedX()"
                  [attr.x2]="selectedX()"
                  y1="0"
                  [attr.y2]="svgHeight()"
                  [attr.aria-label]="selectionLineAria()"
                  (contextmenu)="clearSelection($event)"
                >
                  <title>{{ selectionTimeTooltip() }}</title>
                </line>
                <line
                  class="selection-line"
                  [attr.x1]="selectedX()"
                  [attr.x2]="selectedX()"
                  y1="0"
                  [attr.y2]="svgHeight()"
                />
              }
            </g>

            <g class="layer-now">
              @if (nowX() != null) {
                <rect class="now-past-dim" x="0" y="0" [attr.width]="nowX()" [attr.height]="svgHeight()" />
                <line
                  class="now-line"
                  [attr.x1]="nowX()"
                  [attr.x2]="nowX()"
                  y1="0"
                  [attr.y2]="svgHeight()"
                  [attr.aria-label]="nowLineAria()"
                />
              }
            </g>
          </svg>
        </div>
      </div>

      <!-- Ruler-footer: zelfde ticks onder de lanes, gespiegeld; sticky bottom. -->
      <div class="ephemeris-lanes__ruler ephemeris-lanes__ruler--bottom">
        <div class="ephemeris-lanes__ruler-spacer" [style.width.px]="planetsWidthPx()"></div>
        <div class="ephemeris-lanes__ruler-viewport" (click)="onRulerTimelineClick($event)">
          <ng-container *ngTemplateOutlet="rulerSvg; context: { bottom: true }"></ng-container>
        </div>
      </div>
    }

    @if (loading()) {
      <div class="ephemeris-lanes__loading" role="status" [attr.aria-label]="t('loadingLanes')">
        <e-splash-loader [size]="56" />
      </div>
    } @else if (error() != null) {
      <p class="ephemeris-lanes__error" role="alert">{{ t('errorState') }}</p>
    } @else if (isEmpty()) {
      <p class="ephemeris-lanes__empty">{{ t('emptyState') }}</p>
    }

    @if (showBody() && rangeLimitReached()) {
      <p class="ephemeris-lanes__range-limit" role="status">{{ t('rangeLimitReached') }}</p>
    }
  </div>
</section>
